/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — HeroIllustration (real PNG / SVG, ANA-DIRECT overnight 2026-09-12)
 * ----------------------------------------------------------------------------
 * Staging-only component (feature/site-preview-staging).
 * Replaces HeroCellEmoji for ChoixHeros integration of real Ana assets.
 *
 * Strategy (Track B, 2026-09-13):
 *   - Pull pose asset from /heroes/<personnage>/ via hero-asset-map.ts v1.1
 *   - Default pose: 'idle' (backwards-compatible)
 *   - Fallback to emoji if image fails to load (network error / 404)
 *   - Preserves accessibility (aria-hidden, data-hero-id, data-pose)
 *
 * Production (Wave 3+) will use real semantic states per ninja-ingest-mapping.json
 *   pattern (idle / success_micro / success_major / regulate / recovery / thinking).
 * ========================================================================== */

import React, { useState } from 'react';
import type { HeroCell } from './placeholders';
import { getHeroPoseAsset, type HeroPose } from './hero-asset-map';

export interface HeroIllustrationProps {
  cell: HeroCell;
  /** Pose sémantique (alignée ninja-ingest-mapping.json). Default: 'idle'. */
  pose?: HeroPose;
}

/**
 * HeroIllustration — displays the pose-aware PNG asset for a hero.
 * Falls back to the legacy emoji placeholder if the image fails to load.
 */
export function HeroIllustration({ cell, pose = 'idle' }: HeroIllustrationProps): JSX.Element {
  const asset = getHeroPoseAsset(cell.id, pose);
  const [errored, setErrored] = useState<boolean>(false);

  if (errored) {
    return (
      <div
        data-hero-id={cell.id}
        data-pose={pose}
        data-placeholder="fallback-emoji"
        style={{
          display: 'grid',
          placeItems: 'center',
          width: '100%',
          aspectRatio: '1 / 1',
          background: `var(${cell.accent})`,
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-magical)',
        }}
        aria-hidden="true"
      >
        <span style={{ fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', lineHeight: 1 }}>
          {cell.emoji}
        </span>
      </div>
    );
  }

  return (
    <img
      src={asset.src}
      alt={`${cell.label} (illustration)`}
      data-hero-id={cell.id}
      data-pose={asset.pose}
      data-asset-display={asset.displayName}
      data-asset-placeholder={asset.isPlaceholder ? 'true' : 'false'}
      onError={() => setErrored(true)}
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        aspectRatio: '1 / 1',
        objectFit: 'contain',
        background: `var(${cell.accent})`,
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-magical)',
      }}
    />
  );
}
